import { Address, decodeAbiParameters, formatEther, formatUnits, Hex, isAddressEqual, parseAbiParameters } from 'viem';
import { API_URL as BASE_URL, GIVEAWAY_POLICY_CONTRACT_ADDRESS, REWARD_POLICY_CONTRACT_ADDRESS, USDC_CONTRACT_ADDRESS, ZERO_ADDRESS } from './constants';
import { apiAuthTokenStorage } from './storages';
import { TOKENS } from './tokens';
import { sortLargeArray } from './utils/sortLargeArray';

export const isTokenValid = (token: string, buffer: number = 0): boolean => {
  const tokenData = JSON.parse(atob(token.split('.')[1]));
  const expirationTime = tokenData.exp * 1000; // Convert to milliseconds
  return Date.now() < (expirationTime - buffer)
};

export const getHeaders = (address?: Address): HeadersInit => {
  const token = apiAuthTokenStorage.get(address!);
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
  };
  if (token && isTokenValid(token)) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  return headers;
};

export const getOrThrow = async (response: Response, throwErrorMessage: string) => {
  const r = await response.json();

  if (!response.ok) {
    if (r?.error)
      throw new Error(r.error)
    throw new Error(throwErrorMessage);
  }

  return r;
}

export const parseUserProfile = (address: Address, profile: any): UserProfile => {
  profile.twitterConnected = Boolean(profile.twitterHandle)
  profile.name = profile.twitterConnected ? profile.twitterHandle : 'blinko' + address?.slice(2, 6)
  profile.upvoted = Boolean(profile.isFollowingWitty)
  if (profile.picture) {
    profile.picture = profile.picture.replace('_normal', '_400x400')
  }
  profile.metadata = JSON.parse(profile.metadata || '{}')
  return profile;
}

const api = {
  isLoginTokenValid: (address: Address): boolean => {
    const token = apiAuthTokenStorage.get(address);
    // 토큰 만료되기 하루 전에 미리 요청하기
    return Boolean(token) && isTokenValid(token!, 1000 * 60 * 60 * 24);
  },

  login: async (address: Address, signature: string): Promise<string> => {
    const response = await fetch(`${BASE_URL}/login`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ 
        player: address, 
        signature,
      }),
    });

    const { token } = await getOrThrow(response, 'Failed to login');

    apiAuthTokenStorage.set(address, token);
    return token;
  },

  initTwitterAuth: async (address: Address) => {
    const response = await fetch(`${BASE_URL}/auth/twitter/init`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({
        callbackUrl: `${window.location.origin}/twitter-callback`
      })
    });

    return await getOrThrow(response, 'Failed to initialize Twitter auth');
  },

  completeTwitterAuth: async (address: Address, oauthToken: string, oauthVerifier: string) => {
    const response = await fetch(`${BASE_URL}/auth/twitter/authorize`, {
      method: 'POST',
      headers: getHeaders(address),
      body: JSON.stringify({
        oauthToken,
        oauthVerifier
      })
    });

    const res = await getOrThrow(response, 'Failed to complete Twitter auth');

    try {
      await fetch(`${BASE_URL}/auth/twitter/follow`, {
        method: 'POST',
        headers: getHeaders(address)
      });
    } catch (error) {
      console.error('Failed to follow Twitter', error);
    }

    return res;
  },

  estimateMultiple: async (word: string, maxPlayCount: number) => {
    const response = await fetch(`${BASE_URL}/estimate-win-probability`, {
      method: 'POST',
      headers: getHeaders(),
      body: JSON.stringify({
        word: word,
        maxPlayCount: maxPlayCount,
      }),
    });

    const { probability } = await getOrThrow(response, 'Failed to estimate win probability');
    const adjustedWinProbability = Math.max(Math.min(probability, 1), 0.04);
    if (adjustedWinProbability > 0.99)
      return 1;

    const multiple = (1 / adjustedWinProbability) + 1;
    return parseFloat(multiple.toFixed(1));
  }
};

export default api;

export enum PuzzleType {
  Lingo = 0,
  ___ = 1,
  MoonSheepCannon = 2,
}

export type CreatedPuzzleResponse = {
  quality: number | null;
  answer: string;
  createdAt: number;
  creator: Address;
  currency: Address;
  deadline: number;
  expiryTimestamp: number | null;
  lives: number;
  maxPlayCount: number;
  playCount: number;
  playSolution: Hex;
  playHistory: { 
    guess: string, 
    pattern: ('green' | 'blue' | 'gray')[], 
    solved: boolean, 
    playSolution?: string, 
    payoutData?: Hex,
    payoutSignature?: Hex,
    player: Address, 
    playCount: number, 
    plays: number, 
    timestamp: number

    // moonsheep cannon
    giveawayReward: {
      answer: Address;
      creator: Address;
      currency: Address;
      deadline: number;
      lives: number;
      public: boolean;
      puzzleType: number;
      rewardData: Hex;
      rewardPolicy: Address;
      signature: Hex;
      solution: Hex;
      timeLimit: number;
    } | null
    power: number
    metadata: {
      simulationDetails: {
        angle: number,
        distance: number,
        finalReason: GameOverReason,
        isDead: boolean,
        score: number,
        seed: number,
        totalFrames: number,
      },
      point: number,
      signature: Hex,
      tollRaw: string
      version: 'v1' | 'v2' | 'v3'
    }
  }[];
  player: Address | null;
  plays: number;
  problem: string;
  puzzleId: Address;
  puzzleType: PuzzleType;
  rewardData: string;
  rewardPolicy: Address;
  signature: Hex;
  timeLimit: number;
  valid: boolean;
  wordLength: number;
  title: string;
  hint: string;
  maximumToll: string;
  wordDifficulty: 'normal' | 'extreme';
  wordMode: 'classic_play' | 'classic_plus' | 'alpha_bet' | 'baby_words' | 'big_boy_words';
}

export type CreatedPuzzle = CreatedPuzzleResponse & {
  tollMaximum: bigint;
  tollMinimum: bigint;
  tollType: 'paid' | 'free';
  multiple: bigint;
  creatorExpirePendingPuzzles: CreatedPuzzle[];
  debugLocalServerTimeDiffSeconds?: number;
}

export type PracticePuzzleResponse = {
  puzzleId: string;
  multiplier: number; // 정수값. 몇배 게임인지에 대한 정보
  betCandy: number | null; // null 이면 게임 시작 안한 상태, number 라면 해당 값 만큼 베팅한 상태
  playStatus: 0 | 1 | 2; // 0: not started, 1: in progress, 2: completed
  player: Address;
  playCount: CreatedPuzzle['playCount'];
  createdAt: CreatedPuzzle['createdAt'];
  playHistory: { 
    guess: string, 
    pattern: ('green' | 'blue' | 'gray')[], 
    solved: boolean, 
    playCount: number, 
    timestamp: number 
  }[];
  maxPlayCount: CreatedPuzzleResponse['maxPlayCount'];
  wordLength: CreatedPuzzleResponse['wordLength'];
  playSolution: CreatedPuzzleResponse['playSolution'] | null;
  wordDifficulty: CreatedPuzzleResponse['wordDifficulty'];
}

export type PracticePuzzle = PracticePuzzleResponse & {
  currency: string;
}

export type UserProfileResponse = {
  address: Address;
  name: string;
  picture: string;
  twitterConnected: boolean;
  upvoted: boolean;
  candy: number;
  metadata: {
    tutorialStatus?: 'not_started' | 'in_progress' | 'completed';
    sheepCannonStatus?: {
      distance: number;
      metadata: {
        point: number;
        currentStreak: number;
        maxStreak: number;
        obstacles: { [key in ObstacleType | KillZoneType]: number }
        entryIndex: number;
        lastPlayDate: string;
        maximumPayoutPercent: number;
        totalPlayCount: number;
        totalToll: number;
        dailyRankings: { [key: string]: number } | undefined
        dailyProfitTopRankings: { [key: string]: number } | undefined // top - pnl
        dailyProfitBottomRankings: { [key: string]: number } | undefined // top + pnl
        pointRewardTickets: number;
        tickets: number;
        votedEpochs: { [key:string]: boolean }
      }
    };
  } | null;
}

export type UserProfile = UserProfileResponse & {
}
